import {
  type RemixiconComponentType,
  RiArchive2Line,
  RiArchiveLine,
  RiArrowLeftRightLine,
  RiBillLine,
  RiBook2Line,
  RiBox3Line,
  RiBuildingLine,
  RiDownload2Line,
  RiExchangeDollarLine,
  RiExchangeLine,
  RiInboxUnarchiveLine,
  RiSecurePaymentLine,
  RiSeedlingLine,
  RiStackLine,
  RiUpload2Line,
} from '@remixicon/react';

import type { AccentKey } from '../../constants/collections';

/**
 * accentKey ─▶ icon + 12%-alpha tinted box + solid accent glyph.
 * `constants/collections.ts` picks the key; this table picks its look.
 * Type is never colour-only — status pill + reference id carry it too.
 */
export interface Accent {
  icon: RemixiconComponentType;
  /** Tinted icon-box background (accent @ 12%). */
  box: string;
  /** Solid accent for the glyph. */
  fg: string;
}

export const ACCENT: Record<AccentKey, Accent> = {
  'sales-order': { icon: RiUpload2Line, box: 'bg-[rgba(102,114,57,0.12)]', fg: 'text-[#667239]' },
  'purchase-order': { icon: RiDownload2Line, box: 'bg-[rgba(25,113,194,0.12)]', fg: 'text-[#1971C2]' },
  'buysell-order': { icon: RiExchangeLine, box: 'bg-[rgba(121,136,68,0.12)]', fg: 'text-[#798844]' },
  'work-order': { icon: RiSeedlingLine, box: 'bg-[rgba(9,146,80,0.12)]', fg: 'text-[#099250]' },
  product: { icon: RiBox3Line, box: 'bg-[rgba(9,146,80,0.12)]', fg: 'text-[#099250]' },
  lot: { icon: RiArchive2Line, box: 'bg-[rgba(154,91,46,0.12)]', fg: 'text-[#9A5B2E]' },
  'production-run': { icon: RiStackLine, box: 'bg-[rgba(199,117,20,0.12)]', fg: 'text-[#C77514]' },
  invoice: { icon: RiBillLine, box: 'bg-[rgba(112,72,232,0.12)]', fg: 'text-[#7048E8]' },
  payable: { icon: RiSecurePaymentLine, box: 'bg-[rgba(14,148,136,0.12)]', fg: 'text-[#0E9488]' },
  payment: { icon: RiExchangeDollarLine, box: 'bg-[rgba(67,86,214,0.12)]', fg: 'text-[#4356D6]' },
  'inventory-transfer': { icon: RiArrowLeftRightLine, box: 'bg-[rgba(14,124,168,0.12)]', fg: 'text-[#0E7CA8]' },
  'receiving-ticket': { icon: RiInboxUnarchiveLine, box: 'bg-[rgba(25,113,194,0.12)]', fg: 'text-[#1971C2]' },
  'pick-ticket': { icon: RiArchiveLine, box: 'bg-[rgba(102,114,57,0.12)]', fg: 'text-[#667239]' },
  'ledger-account': { icon: RiBook2Line, box: 'bg-[rgba(162,62,140,0.12)]', fg: 'text-[#A23E8C]' },
  'business-entity': { icon: RiBuildingLine, box: 'bg-[rgba(91,107,134,0.12)]', fg: 'text-[#5B6B86]' },
};

const DEFAULT_ACCENT: Accent = { icon: RiBox3Line, box: 'bg-neutral-3', fg: 'text-neutral-9' };

// Widened-index view so a plain string (e.g. a stale localStorage recent) can be
// looked up without a cast; unknown keys fall back to the neutral accent.
const ACCENT_BY_STRING: Record<string, Accent> = ACCENT;

/** Resolve an accent, tolerating an unknown key (e.g. a stale localStorage recent). */
export function resolveAccent(key: string): Accent {
  return ACCENT_BY_STRING[key] ?? DEFAULT_ACCENT;
}
